import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  X, Printer, Share2, RefreshCw, FileText, AlertCircle, 
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function CustomerLedgerModal({
  customer,
  tenantSlug = 'samarth-krupa-dairy',
  currentTenant,
  lang = 'mr',
  onClose,
  initialMonth
}) {
  const isMr = lang === 'mr';

  const defaultDates = useMemo(() => {
    const now = new Date();
    const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const targetYm = initialMonth || currentYm;
    const [yr, mo] = targetYm.split('-');
    const daysInMo = new Date(parseInt(yr, 10), parseInt(mo, 10), 0).getDate();

    const from = `${targetYm}-01`;
    let to = `${targetYm}-${String(daysInMo).padStart(2, '0')}`;
    
    if (targetYm === currentYm) {
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      to = todayStr;
    }

    return { from, to };
  }, [initialMonth]);

  const [fromDate, setFromDate] = useState(defaultDates.from);
  const [toDate, setToDate] = useState(defaultDates.to);
  const [loading, setLoading] = useState(true);
  const [ledgerData, setLedgerData] = useState(null);
  const [error, setError] = useState('');

  const customerId = customer?.customerId || customer?.id;
  const customerName = isMr 
    ? (customer?.customerName || customer?.name_mr || customer?.name || `ग्राहक #${customerId}`)
    : (customer?.customerNameEn || customer?.name_en || customer?.name || `Customer #${customerId}`);

  const dairyName = isMr 
    ? (currentTenant?.brand_name_mr || currentTenant?.brandMarathi || currentTenant?.name || 'समर्थ कृपा डेअरी')
    : (currentTenant?.brand_name_en || currentTenant?.brandEnglish || currentTenant?.name || 'Samarth Krupa Dairy');

  const dairyAddress = currentTenant?.address || currentTenant?.city || '';
  const dairyPhone = currentTenant?.phone || '';

  // Delete single entry directly from Customer Ledger Statement
  const handleDeleteEntry = async (item) => {
    const dFmt = item.date.split('-').reverse().join('/');
    const confirmMsg = isMr
      ? '⚠️ खात्री करा:\nदिनांक ' + dFmt + ' ची \'' + item.description + '\' ही ₹' + (item.debit || item.credit) + ' ची नोंद खातेउताऱ्यातून कायमची हटवायची आहे का?\n\n(ही नोंद हटवल्यास रोजनिशी व मासिक हजेरीतून ती आपोआप रद्द होईल व शिल्लक बाकी री-कॅल्क्युलेट होईल.)'
      : 'Are you sure you want to permanently delete the entry for ' + dFmt + ' (' + item.description + ')?';

    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/billing/delete-entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          date: item.date,
          category: item.category,
          type: item.type
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || 'नोंद यशस्वीरित्या हटवली.');
        fetchLedger(fromDate, toDate);
      } else {
        alert(data.message || 'नोंद हटवण्यात त्रुटी आली.');
      }
    } catch (err) {
      alert('सर्व्हरशी संपर्क होऊ शकला नाही: ' + err.message);
    }
  };

  // Fetch Ledger from Backend
  const fetchLedger = useCallback(async (fDate, tDate) => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    try {
      const url = `${API_BASE}/tenant/${tenantSlug}/customers/${customerId}/ledger?from_date=${fDate || fromDate}&to_date=${tDate || toDate}`;
      const res = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!res.ok) {
        let errMsg = `सर्व्हर एरर (HTTP ${res.status})`;
        try {
          const errObj = await res.json();
          if (errObj && errObj.message) errMsg = errObj.message;
        } catch (e) {
          errMsg = `माहिती लोड करताना एरर आली (Status ${res.status}). कृपया पुन्हा प्रयत्न करा.`;
        }
        setError(errMsg);
        return;
      }

      const data = await res.json();
      if (data.success) {
        setLedgerData(data);
      } else {
        setError(data.message || 'खातेउतारा उपलब्ध होऊ शकला नाही');
      }
    } catch (err) {
      console.error('Failed to fetch ledger:', err);
      setError('सर्व्हरशी संपर्क होऊ शकला नाही. कृपया इंटरनेट तपासून पुन्हा प्रयत्न करा.');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, customerId, fromDate, toDate]);

  useEffect(() => {
    fetchLedger(fromDate, toDate);
  }, [fetchLedger, fromDate, toDate]);

  // Quick Presets
  const handleQuickPreset = (preset) => {
    const now = new Date();
    if (preset === 'THIS_MONTH') {
      const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const today = `${ym}-${String(now.getDate()).padStart(2, '0')}`;
      setFromDate(`${ym}-01`);
      setToDate(today);
    } else if (preset === 'LAST_MONTH') {
      const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const ym = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
      const days = new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 0).getDate();
      setFromDate(`${ym}-01`);
      setToDate(`${ym}-${String(days).padStart(2, '0')}`);
    } else if (preset === 'LAST_3_MONTHS') {
      const prevDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const ym = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      setFromDate(`${ym}-01`);
      setToDate(today);
    }
  };

  // WhatsApp Share with DIRECT STATEMENT LINK
  const handleWhatsAppShare = () => {
    if (!ledgerData) return;

    const fromFmt = fromDate.split('-').reverse().join('/');
    const toFmt = toDate.split('-').reverse().join('/');
    const phone = (customer?.phone || ledgerData.customer?.phone || '').replace(/\D/g, '');

    const baseUrl = window.location.origin + window.location.pathname;
    const publicLink = `${baseUrl}#statement-view?t=${tenantSlug}&c=${customerId}&from=${fromDate}&to=${toDate}`;

    const text = isMr ? `*🥛 ${dairyName} - ग्राहक खातेउतारा*
*(Statement of Account)*
कालावधी: *${fromFmt} ते ${toFmt}*
----------------------------------------
ग्राहक: *${customerName}* (ID: #${customerId})
मोबाईल: ${customer?.phone || ledgerData.customer?.phone || '-'}
मार्ग: ${customer?.lineName || customer?.line_name || ledgerData.customer?.line_name || '-'}
----------------------------------------
आरंभी बाकी (Opening): ₹${Number(ledgerData.opening_balance).toLocaleString()} Dr
एकूण नावे (Total Dr): ₹${Number(ledgerData.total_debits).toLocaleString()}
एकूण जमा (Total Cr): ₹${Number(ledgerData.total_credits).toLocaleString()}
----------------------------------------
${Number(ledgerData.closing_balance) > 0 ? `👉 *अंतिम बाकी शिल्लक: ₹${Number(ledgerData.closing_balance).toLocaleString()} Dr*` : `✅ *पूर्ण हिशोब चुकता: ₹0 बाकी*`}
----------------------------------------
📄 *आपला सविस्तर खातेउतारा (PDF) पाहण्यासाठी व डाऊनलोड करण्यासाठी खालील लिंकवर क्लिक करा:*
👉 ${publicLink}
----------------------------------------
*॥ ${dairyName} ॥*`
      : `*🥛 ${dairyName} - Customer Statement*
Period: *${fromFmt} to ${toFmt}*
----------------------------------------
Customer: *${customerName}* (ID: #${customerId})
Phone: ${customer?.phone || ledgerData.customer?.phone || '-'}
----------------------------------------
Opening Balance: ₹${Number(ledgerData.opening_balance).toLocaleString()} Dr
Total Debit: ₹${Number(ledgerData.total_debits).toLocaleString()}
Total Credit: ₹${Number(ledgerData.total_credits).toLocaleString()}
----------------------------------------
${Number(ledgerData.closing_balance) > 0 ? `👉 *Net Closing Balance: ₹${Number(ledgerData.closing_balance).toLocaleString()} Dr*` : `✅ *Fully Settled: ₹0 Due*`}
----------------------------------------
📄 *View and Download your Statement PDF here:*
👉 ${publicLink}
----------------------------------------
*${dairyName}*`;

    const encoded = encodeURIComponent(text);
    if (phone && phone.length === 10) {
      window.open(`https://wa.me/91${phone}?text=${encoded}`, '_blank');
    } else {
      window.open(`https://wa.me/?text=${encoded}`, '_blank');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const fromFmt = fromDate.split('-').reverse().join('/');
  const toFmt = toDate.split('-').reverse().join('/');

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.72)',
      backdropFilter: 'blur(5px)',
      WebkitBackdropFilter: 'blur(5px)',
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px'
    }}>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-customer-ledger, #printable-customer-ledger * {
            visibility: visible;
          }
          #printable-customer-ledger {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 8px 12px;
            background: #ffffff !important;
            box-shadow: none !important;
            border: none !important;
          }
          .no-print {
            display: none !important;
          }
          .print-header-block {
            display: block !important;
          }
          .ledger-print-table th, .ledger-print-table td {
            border: 1px solid #334155 !important;
            padding: 4px 6px !important;
            font-size: 11px !important;
            line-height: 1.25 !important;
          }
        }
      `}</style>

      <div 
        id="printable-customer-ledger"
        style={{
          background: '#ffffff',
          borderRadius: 18,
          width: '100%',
          maxWidth: 960,
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          border: '1.5px solid #cbd5e1'
        }}
      >
        {/* ============================================================
            1. MODAL HEADER (डेअरी व ग्राहक ओळख पट्टी - No print)
            ============================================================ */}
        <div className="no-print" style={{
          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
          color: '#ffffff',
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '2px solid #0284c7'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: '#ffffff',
              color: '#0284c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
            }}>
              <FileText size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.2rem', fontWeight: 900, letterSpacing: '-0.3px' }}>
                  {customerName}
                </span>
                <span style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  padding: '2px 8px', 
                  borderRadius: 6, 
                  fontSize: '0.74rem', 
                  fontWeight: 800 
                }}>
                  ID: #{customerId}
                </span>
                <span style={{ 
                  background: '#fef08a', 
                  color: '#854d0e',
                  padding: '2px 8px', 
                  borderRadius: 6, 
                  fontSize: '0.72rem', 
                  fontWeight: 800 
                }}>
                  {isMr ? 'खातेउतारा' : 'Account Statement'}
                </span>
              </div>
              <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>📱 {customer?.phone || ledgerData?.customer?.phone || '-'}</span>
                <span>📍 {customer?.address || ledgerData?.customer?.address || '-'}</span>
                <span>🛣️ {customer?.lineName || customer?.line_name || ledgerData?.customer?.line_name || 'मार्ग क्र. १'}</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={handlePrint}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#ffffff',
                padding: '7px 14px',
                borderRadius: 8,
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              title={isMr ? 'प्रिंट / PDF डाऊनलोड' : 'Print / Download PDF'}
            >
              <Printer size={15} />
              <span>{isMr ? 'प्रिंट / PDF' : 'Print / PDF'}</span>
            </button>

            <button
              type="button"
              onClick={handleWhatsAppShare}
              style={{
                background: '#16a34a',
                border: 'none',
                color: '#ffffff',
                padding: '7px 14px',
                borderRadius: 8,
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
              }}
              title={isMr ? 'व्हॉट्सॲपवर पाठवा' : 'Share on WhatsApp'}
            >
              <Share2 size={15} />
              <span>WhatsApp</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: '#ffffff',
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginLeft: 4
              }}
              title={isMr ? 'बंद करा' : 'Close'}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* PRINT HEADER BLOCK (फक्त A4 प्रिंटसाठी) */}
        <div style={{ display: 'none', padding: '10px 0', borderBottom: '2px solid #0f172a', marginBottom: 8 }} className="print-header-block">
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 900, color: '#0f172a' }}>॥ {dairyName} ॥</h2>
            <div style={{ fontSize: '0.8rem', color: '#475569' }}>{dairyAddress} {dairyPhone ? `| फोन: ${dairyPhone}` : ''}</div>
            <div style={{ fontSize: '0.92rem', fontWeight: 900, marginTop: 4, textTransform: 'uppercase' }}>
              ग्राहक खातेउतारा (STATEMENT OF ACCOUNT)
            </div>
            <div style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 700, marginTop: 2 }}>
              कालावधी: {fromFmt} ते {toFmt}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginTop: 8, padding: '6px 10px', background: '#f8fafc', borderRadius: 6, border: '1px solid #cbd5e1' }}>
            <div>
              <strong>ग्राहक नाव:</strong> {customerName} (ID: #{customerId})<br />
              <strong>पत्ता:</strong> {customer?.address || ledgerData?.customer?.address || '-'}
            </div>
            <div style={{ textAlign: 'right' }}>
              <strong>मोबाईल:</strong> {customer?.phone || ledgerData?.customer?.phone || '-'}<br />
              <strong>मार्ग / लाईन:</strong> {customer?.lineName || customer?.line_name || ledgerData?.customer?.line_name || '-'}
            </div>
          </div>
        </div>

        {/* ============================================================
            2. DATE FILTER BAR (तारीख निवड पट्टी)
            ============================================================ */}
        <div className="no-print" style={{
          background: '#f8fafc',
          padding: '10px 18px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 700 }}>
                {isMr ? 'दिनांक पासून:' : 'From:'}
              </span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  outline: 'none',
                  background: '#ffffff'
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 700 }}>
                {isMr ? 'दिनांक पर्यंत:' : 'To:'}
              </span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  outline: 'none',
                  background: '#ffffff'
                }}
              />
            </div>

            <button
              type="button"
              onClick={() => fetchLedger(fromDate, toDate)}
              disabled={loading}
              style={{
                background: '#0284c7',
                border: 'none',
                color: '#ffffff',
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <RefreshCw size={13} className={loading ? 'spin' : ''} />
              <span>{isMr ? 'शोधा' : 'Filter'}</span>
            </button>
          </div>

          {/* Quick Presets */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>
              {isMr ? 'झटपट:' : 'Quick:'}
            </span>
            <button
              type="button"
              onClick={() => handleQuickPreset('THIS_MONTH')}
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                color: '#0f172a'
              }}
            >
              {isMr ? 'चालू महिना' : 'This Month'}
            </button>
            <button
              type="button"
              onClick={() => handleQuickPreset('LAST_MONTH')}
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                color: '#0f172a'
              }}
            >
              {isMr ? 'मागील महिना' : 'Last Month'}
            </button>
            <button
              type="button"
              onClick={() => handleQuickPreset('LAST_3_MONTHS')}
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                color: '#0f172a'
              }}
            >
              {isMr ? 'मागील ३ महिने' : 'Last 3 Mos'}
            </button>
          </div>
        </div>

        {/* ============================================================
            3. STANDARD ACCOUNTING STATEMENT SUMMARY STRIP
            ============================================================ */}
        {ledgerData && (
          <div style={{
            background: '#f1f5f9',
            padding: '8px 18px',
            borderBottom: '1px solid #cbd5e1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.82rem',
            fontWeight: 800,
            flexWrap: 'wrap',
            gap: 10
          }}>
            <div style={{ color: '#0369a1' }}>
              <span>{isMr ? 'आरंभी बाकी:' : 'Opening Bal:'}</span>{' '}
              <span style={{ fontWeight: 900 }}>₹{Number(ledgerData.opening_balance).toLocaleString()} Dr</span>
            </div>
            <div style={{ color: '#4338ca' }}>
              <span>{isMr ? 'एकूण नावे (Total Dr):' : 'Total Debit:'}</span>{' '}
              <span style={{ fontWeight: 900 }}>₹{Number(ledgerData.total_debits).toLocaleString()}</span>
            </div>
            <div style={{ color: '#15803d' }}>
              <span>{isMr ? 'एकूण जमा (Total Cr):' : 'Total Credit:'}</span>{' '}
              <span style={{ fontWeight: 900 }}>₹{Number(ledgerData.total_credits).toLocaleString()}</span>
            </div>
            <div style={{ 
              color: Number(ledgerData.closing_balance) > 0 ? '#b91c1c' : '#15803d',
              background: '#ffffff',
              padding: '3px 10px',
              borderRadius: 6,
              border: Number(ledgerData.closing_balance) > 0 ? '1px solid #fca5a5' : '1px solid #86efac'
            }}>
              <span>{isMr ? 'अंतिम बाकी:' : 'Closing Bal:'}</span>{' '}
              <span style={{ fontWeight: 900 }}>₹{Number(ledgerData.closing_balance).toLocaleString()} Dr</span>
            </div>
          </div>
        )}

        {/* ============================================================
            4. STANDARD 5-COLUMN ACCOUNTING LEDGER TABLE (3rd column removed, wide single-line particulars)
            Columns: Date | Particulars (Single line) | Debit (₹) | Credit (₹) | Running Balance (₹)
            ============================================================ */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px 18px',
          background: '#ffffff'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px 0', color: '#64748b' }}>
              <RefreshCw size={26} className="spin" style={{ margin: '0 auto 8px auto', color: '#0284c7' }} />
              <div style={{ fontWeight: 800, fontSize: '0.88rem' }}>
                {isMr ? 'खातेउतारा लोड होत आहे...' : 'Loading account statement...'}
              </div>
            </div>
          ) : error ? (
            <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', padding: '16px', borderRadius: 10, color: '#b91c1c', textAlign: 'center', margin: '20px 0' }}>
              <AlertCircle size={24} style={{ margin: '0 auto 6px auto' }} />
              <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>{error}</div>
              <button
                type="button"
                onClick={() => fetchLedger(fromDate, toDate)}
                style={{
                  marginTop: 10,
                  background: '#b91c1c',
                  color: '#fff',
                  border: 'none',
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                {isMr ? 'पुन्हा प्रयत्न करा' : 'Retry'}
              </button>
            </div>
          ) : (
            <table className="ledger-print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#0f172a', color: '#ffffff', textAlign: 'left' }}>
                  <th style={{ padding: '8px 10px', fontWeight: 800, width: 95 }}>{isMr ? 'दिनांक' : 'Date'}</th>
                  <th style={{ padding: '8px 10px', fontWeight: 800 }}>{isMr ? 'तपशील / विवरण (Particulars)' : 'Particulars'}</th>
                  <th style={{ padding: '8px 10px', fontWeight: 800, textAlign: 'right', width: 105 }}>{isMr ? 'नावे (Dr ₹)' : 'Debit (₹)'}</th>
                  <th style={{ padding: '8px 10px', fontWeight: 800, textAlign: 'right', width: 105 }}>{isMr ? 'जमा (Cr ₹)' : 'Credit (₹)'}</th>
                  <th style={{ padding: '8px 10px', fontWeight: 800, textAlign: 'right', width: 120 }}>{isMr ? 'शिल्लक बाकी' : 'Balance (₹)'}</th>
                </tr>
              </thead>
              <tbody>
                {/* 1. Opening Balance Row */}
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1', fontWeight: 800 }}>
                  <td style={{ padding: '8px 10px', color: '#475569', whiteSpace: 'nowrap' }}>{fromFmt}</td>
                  <td style={{ padding: '8px 10px', color: '#0f172a' }}>
                    {isMr ? `आरंभी बाकी (Opening Balance - ${fromFmt} पूर्वीची शिल्लक)` : `Opening Balance (Prior to ${fromFmt})`}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#0369a1', fontWeight: 800 }}>
                    ₹{Number(ledgerData ? ledgerData.opening_balance : 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#94a3b8' }}>-</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#0369a1', fontWeight: 900 }}>
                    ₹{Number(ledgerData ? ledgerData.opening_balance : 0).toLocaleString()} Dr
                  </td>
                </tr>

                {/* 2. Chronological Ledger Rows (Only transactions, 0L/खाडा omitted) */}
                {ledgerData?.entries && ledgerData.entries.length > 0 ? (
                  ledgerData.entries.map((item, idx) => {
                    const isDebit = item.type === 'DEBIT';
                    const isCredit = item.type === 'CREDIT';
                    const dFmt = item.date.split('-').reverse().join('/');

                    return (
                      <tr 
                        key={idx} 
                        style={{ 
                          borderBottom: '1px solid #e2e8f0',
                          background: isCredit ? '#f0fdf4' : (idx % 2 === 0 ? '#ffffff' : '#fafafa')
                        }}
                      >
                        <td style={{ padding: '8px 10px', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
                          {dFmt}
                        </td>
                        <td style={{ 
                          padding: '8px 10px', 
                          color: '#0f172a',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 420
                        }} title={item.description}>
                          <span style={{ fontWeight: 600 }}>{item.description}</span>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, color: isDebit && item.debit > 0 ? '#4338ca' : '#94a3b8' }}>
                          {isDebit && item.debit > 0 ? `₹${Number(item.debit).toLocaleString()}` : '-'}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, color: isCredit && item.credit > 0 ? '#15803d' : '#94a3b8' }}>
                          {isCredit && item.credit > 0 ? `₹${Number(item.credit).toLocaleString()}` : '-'}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 900, color: (item.balance || 0) > 0 ? '#b91c1c' : '#15803d' }}>
                          ₹{Number(item.balance || 0).toLocaleString()} {(item.balance || 0) > 0 ? 'Dr' : ''}
                        </td>
                        <td className="no-print" style={{ padding: '4px 6px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleDeleteEntry(item)}
                            style={{
                              background: '#fee2e2',
                              border: '1px solid #fca5a5',
                              color: '#dc2626',
                              borderRadius: 6,
                              width: 28,
                              height: 28,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                            title={isMr ? "ही नोंद खातेउताऱ्यातून हटवा" : "Delete entry"}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                      {isMr ? 'या कालावधीत कोणतेही व्यवहार नोंदवलेले नाहीत.' : 'No transactions found in this period.'}
                    </td>
                  </tr>
                )}

                {/* 3. Totals Closing Row */}
                {ledgerData && (
                  <tr style={{ background: '#f8fafc', borderTop: '2px solid #0f172a', fontWeight: 900 }}>
                    <td colSpan={2} style={{ padding: '10px', textAlign: 'right', color: '#0f172a', fontSize: '0.84rem' }}>
                      {isMr ? `एकूण व्यवहार व अंतिम बाकी (${toFmt} अखेर):` : `Total & Closing Balance (As of ${toFmt}):`}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#4338ca', fontSize: '0.84rem' }}>
                      ₹{Number(ledgerData.total_debits).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#15803d', fontSize: '0.84rem' }}>
                      ₹{Number(ledgerData.total_credits).toLocaleString()}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      textAlign: 'right', 
                      fontSize: '0.96rem', 
                      color: Number(ledgerData.closing_balance) > 0 ? '#b91c1c' : '#15803d' 
                    }}>
                      ₹{Number(ledgerData.closing_balance).toLocaleString()} {Number(ledgerData.closing_balance) > 0 ? 'Dr' : ''}
                    </td>
                    <td className="no-print" style={{ padding: '10px' }}></td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* PRINT SIGNATURE BLOCK (फक्त A4 प्रिंटसाठी) */}
        <div style={{ display: 'none', padding: '24px 10px 8px 10px' }} className="print-header-block">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '0.8rem' }}>
            <div>
              <strong>तारीख:</strong> {new Date().toLocaleDateString('en-GB')}<br />
              <small>* संगणकीकृत अधिकृत खातेउतारा | e.Sense MilkPro Mitra</small>
            </div>
            <div style={{ textAlign: 'center', borderTop: '1px solid #475569', width: 160, paddingTop: 4 }}>
              <strong>॥ {dairyName} ॥</strong><br />
              अधिकृत स्वाक्षरी / शिक्का
            </div>
          </div>
        </div>

        {/* ============================================================
            5. MODAL FOOTER (तळाची ॲक्शन पट्टी)
            ============================================================ */}
        <div className="no-print" style={{
          padding: '10px 20px',
          background: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10
        }}>
          <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
            {isMr ? '💡 स्टँडर्ड ५-कॉलम फॉरमॅट: दिनांक, तपशील/विवरण, नावे (Dr), जमा (Cr), अंतिम बाकी.' : 'Standard 5-column format: Date, Particulars, Debit, Credit, Running Balance.'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={handlePrint}
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                color: '#0f172a',
                padding: '7px 14px',
                borderRadius: 8,
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <Printer size={15} />
              <span>{isMr ? 'प्रिंट करा' : 'Print'}</span>
            </button>

            <button
              type="button"
              onClick={handleWhatsAppShare}
              style={{
                background: '#16a34a',
                border: 'none',
                color: '#ffffff',
                padding: '7px 14px',
                borderRadius: 8,
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
              }}
            >
              <Share2 size={15} />
              <span>{isMr ? 'WhatsApp वर पाठवा' : 'Share on WhatsApp'}</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: '#0f172a',
                border: 'none',
                color: '#ffffff',
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer'
              }}
            >
              {isMr ? 'बंद करा' : 'Close'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
